import {jwtDecode} from 'jwt-decode';
import Logger from "./logger";

/**
 * Service centralisé pour la gestion du localStorage
 * Seule source autorisée pour manipuler les données d'authentification
 * 
 * Gère:
 * - Stockage et récupération des tokens
 * - Synchronisation multi-onglets
 * - Validation des tokens
 * - Gestion de l'inactivité
 */

const DEFAULT_NAMESPACE = 'easySurvey';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_NAME || `${DEFAULT_NAMESPACE}.token`;
const REFRESH_TOKEN_KEY = `${DEFAULT_NAMESPACE}.refresh_token`;
const STORAGE_NAMESPACE = TOKEN_KEY.includes('.')
  ? TOKEN_KEY.split('.')[0]
  : DEFAULT_NAMESPACE;
const USER_KEY = `${STORAGE_NAMESPACE}.user`;
const ROLE_KEY = `${STORAGE_NAMESPACE}.role`;

// Clés pour la synchronisation multi-onglets
const LOGOUT_EVENT_KEY = `${DEFAULT_NAMESPACE}.logout_event`;
const LOGIN_EVENT_KEY = `${DEFAULT_NAMESPACE}.login_event`;
const ACTIVITY_EVENT_KEY = `${DEFAULT_NAMESPACE}.last_activity`;

/**
 * Valide si un token JWT est bien formé et non expiré
 * @param {string} token - Token JWT à valider
 * @returns {Object} { isValid: boolean, decoded: Object|null, error: string|null }
 */
const validateToken = (token) => {
  if (!token || typeof token !== 'string') {
    return { isValid: false, decoded: null, error: 'Token invalide ou manquant' };
  }

  try {
    const decoded = jwtDecode(token);
    
    // Vérifier l'expiration
    if (decoded.exp) {
      const expirationTime = decoded.exp * 1000; // Convertir en millisecondes
      if (Date.now() >= expirationTime) {
        return { isValid: false, decoded: null, error: 'Token expiré' };
      }
    }

    // Vérifier la présence du rôle OU du user_id (pour les tokens SSO)
    // Les tokens SSO n'ont pas de rôle dans le JWT mais ont user_id
    if (!decoded.role && !decoded.user_id) {
      return { isValid: false, decoded: null, error: 'Token invalide: pas de rôle ni user_id' };
    }

    return { isValid: true, decoded, error: null };
  } catch (error) {
    return { isValid: false, decoded: null, error: `Token invalide: ${error.message}` };
  }
};

/**
 * Stocke le token d'accès
 * @param {string} token - Token JWT à stocker
 * @param {string} refreshToken - Token de rafraîchissement (optionnel)
 * @throws {Error} Si le token est invalide
 */
export const setToken = (token, refreshToken = null) => {
  if (!token || typeof token !== 'string') {
    throw new Error('Token invalide: doit être une chaîne non vide');
  }

  const validation = validateToken(token);
  if (!validation.isValid) {
    throw new Error(`Impossible de stocker le token: ${validation.error}`);
  }

  try {
    localStorage.setItem(TOKEN_KEY, token);
    
    // Stocker le refresh token s'il est fourni
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    
    // ✅ Signal de connexion pour synchroniser les autres onglets
    localStorage.setItem(LOGIN_EVENT_KEY, Date.now().toString());
    
    Logger.log('[Storage] Token stocké avec succès');
  } catch (error) {
    Logger.error('[Storage] Erreur lors du stockage du token:', error);
    throw new Error(`Erreur de stockage: ${error.message}`);
  }
};

/**
 * Récupère le token d'accès
 * @returns {string|null} Token stocké ou null
 */
export const getToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    // Valider le token avant de le retourner
    const validation = validateToken(token);
    if (!validation.isValid) {
      Logger.warn('[Storage] Token invalide ou expiré, suppression');
      removeToken();
      return null;
    }

    return token;
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la récupération du token:', error);
    return null;
  }
};

/**
 * Supprime le token d'accès
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    Logger.log('[Storage] Token supprimé');
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la suppression du token:', error);
  }
};

/**
 * Stocke les informations utilisateur
 * @param {Object} user - Objet utilisateur
 * @throws {Error} Si l'utilisateur est invalide
 */
export const setUser = (user) => {
  if (!user || typeof user !== 'object') {
    throw new Error('Utilisateur invalide: doit être un objet');
  }

  if (!user.role) {
    throw new Error('Utilisateur invalide: rôle manquant');
  }

  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(ROLE_KEY, user.role);
    Logger.log('[Storage] Utilisateur stocké avec succès');
  } catch (error) {
    Logger.error('[Storage] Erreur lors du stockage de l\'utilisateur:', error);
    throw new Error(`Erreur de stockage: ${error.message}`);
  }
};

/**
 * Récupère les informations utilisateur
 * @returns {Object|null} Objet utilisateur ou null
 */
export const getUser = () => {
  try {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;

    const user = JSON.parse(userJson);
    if (!user.role) {
      Logger.warn('[Storage] Utilisateur sans rôle, suppression');
      removeUser();
      return null;
    }

    return user;
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la récupération de l\'utilisateur:', error);
    removeUser();
    return null;
  }
};

/**
 * Récupère le rôle utilisateur
 * @returns {string|null} Rôle utilisateur ou null
 */
export const getRole = () => {
  try {
    return localStorage.getItem(ROLE_KEY);
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la récupération du rôle:', error);
    return null;
  }
};

/**
 * Supprime les informations utilisateur
 */
export const removeUser = () => {
  try {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_KEY);
    Logger.log('[Storage] Utilisateur supprimé');
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la suppression de l\'utilisateur:', error);
  }
};

/**
 * Supprime tous les données d'authentification
 * Envoie un signal de déconnexion pour synchroniser les autres onglets
 */
export const clearAll = () => {
  try {
    removeToken();
    removeUser();
    
    // ✅ Signal de déconnexion pour synchroniser les autres onglets
    localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
    
    Logger.log('[Storage] Toutes les données d\'authentification supprimées');
  } catch (error) {
    Logger.error('[Storage] Erreur lors du nettoyage complet:', error);
  }
};

/**
 * Vérifie la cohérence entre le token et l'utilisateur stocké
 * @param {boolean} autoClean - Nettoyer automatiquement en cas d'incohérence (défaut: false)
 * @returns {Object} { isCoherent: boolean, error: string|null }
 */
export const verifyCoherence = (autoClean = false) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = getUser();
    const storedRole = localStorage.getItem(ROLE_KEY);

    // Si pas de token, c'est cohérent (pas connecté)
    if (!token) {
      if (user || storedRole) {
        Logger.warn('[Storage] Incohérence: utilisateur sans token');
        if (autoClean) {
          clearAll();
        }
        return { isCoherent: false, error: 'Incohérence détectée: utilisateur sans token' };
      }
      return { isCoherent: true, error: null };
    }

    // Si token existe, vérifier sa validité
    const validation = validateToken(token);
    if (!validation.isValid) {
      Logger.warn('[Storage] Token invalide:', validation.error);
      if (autoClean) {
        clearAll();
      }
      return { isCoherent: false, error: `Token invalide: ${validation.error}` };
    }

    // Vérifier que le rôle du token correspond au rôle stocké
    // Pour les tokens SSO, le rôle n'est pas dans le JWT mais dans le localStorage
    const tokenRole = validation.decoded.role;
    if (storedRole && tokenRole && tokenRole !== storedRole) {
      Logger.warn('[Storage] Incohérence: rôle du token différent du rôle stocké');
      if (autoClean) {
        clearAll();
      }
      return { isCoherent: false, error: 'Incohérence: rôle différent' };
    }

    return { isCoherent: true, error: null };
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la vérification de cohérence:', error);
    if (autoClean) {
      clearAll();
    }
    return { isCoherent: false, error: `Erreur: ${error.message}` };
  }
};

/**
 * Enregistre l'activité de l'utilisateur pour la synchronisation multi-onglets
 * Utilisé pour synchroniser l'inactivité entre les onglets
 */
export const recordActivity = () => {
  try {
    localStorage.setItem(ACTIVITY_EVENT_KEY, Date.now().toString());
  } catch (error) {
    Logger.error('[Storage] Erreur lors de l\'enregistrement de l\'activité:', error);
  }
};

/**
 * Récupère le timestamp de la dernière activité
 * @returns {number} Timestamp en millisecondes
 */
export const getLastActivityTime = () => {
  try {
    const lastActivity = localStorage.getItem(ACTIVITY_EVENT_KEY);
    return lastActivity ? parseInt(lastActivity, 10) : Date.now();
  } catch (error) {
    Logger.error('[Storage] Erreur lors de la récupération de l\'activité:', error);
    return Date.now();
  }
};

/**
 * Décodes le token JWT sans validation
 * Utile pour extraire les informations du token
 * @returns {Object|null} Token décodé ou null
 */
export const decodeToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    return jwtDecode(token);
  } catch (error) {
    Logger.error('[Storage] Erreur lors du décodage du token:', error);
    return null;
  }
};

/**
 * Vérifie si l'utilisateur est authentifié
 * @returns {boolean} true si authentifié, false sinon
 */
export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

/**
 * Récupère les clés d'événements pour la synchronisation multi-onglets
 * @returns {Object} Objet contenant les clés d'événements
 */
export const getEventKeys = () => ({
  LOGOUT_EVENT_KEY,
  LOGIN_EVENT_KEY,
  ACTIVITY_EVENT_KEY,
});

const storage = {
  setToken,
  getToken,
  removeToken,
  setUser,
  getUser,
  removeUser,
  getRole,
  clearAll,
  verifyCoherence,
  decodeToken,
  isAuthenticated,
  recordActivity,
  getLastActivityTime,
  getEventKeys,
};

export default storage;
